'use client';

import { useState, useEffect, useCallback } from 'react';
import { useCustomerAuth } from '@/components/auth/CustomerAuthProvider';
import type { Feedback, CreateFeedbackRequest } from '@/types/customer';
import { PortalLayout } from '../PortalLayout';
import { Card } from '../ui/Card';

export function MyFeedback() {
  const { getCustomerClient, isAuthenticated } = useCustomerAuth();
  const [feedbackList, setFeedbackList] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchFeedback = useCallback(async () => {
    const client = getCustomerClient();
    if (!client) return;
    try {
      setLoading(true);
      const data = await client.getFeedback();
      setFeedbackList(data.items || []);
    } catch (err) {
      console.warn('[MyFeedback] Failed to fetch feedback:', err);
    } finally {
      setLoading(false);
    }
  }, [getCustomerClient]);

  useEffect(() => { if (isAuthenticated) fetchFeedback(); }, [isAuthenticated, fetchFeedback]);

  const handleSubmit = async () => {
    const client = getCustomerClient();
    if (!client || !comment.trim()) return;
    try {
      setSubmitting(true);
      const data: CreateFeedbackRequest = {
        type: 'GENERAL',
        ...(rating ? { rating } : {}),
        comment: comment.trim(),
      };
      await client.createFeedback(data);
      setShowForm(false);
      setRating(0);
      setComment('');
      fetchFeedback();
    } catch (err) {
      console.warn('[MyFeedback] Failed to submit:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortalLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Feedback</h1>
            <p className="text-gray-600 mt-1">Share your experience and view past feedback</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            {showForm ? 'Cancel' : 'Give Feedback'}
          </button>
        </div>

        {showForm && (
          <Card className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Share Your Feedback</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <button key={star} onClick={() => setRating(star)} className={`text-2xl transition-colors ${star <= rating ? 'text-amber-400' : 'text-gray-300 hover:text-amber-200'}`}>
                    ★
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="feedback-comment" className="block text-sm font-medium text-gray-700 mb-2">Comments</label>
              <textarea
                id="feedback-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                placeholder="Tell us about your experience..."
              />
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitting || !comment.trim()}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </Card>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1, 2].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}
          </div>
        ) : feedbackList.length === 0 ? (
          <Card className="p-12 text-center">
            <svg className="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 011.037-.443 48.282 48.282 0 005.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" /></svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No feedback yet</h3>
            <p className="text-gray-500 text-sm">Share your experience by clicking &quot;Give Feedback&quot;</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {feedbackList.map(fb => (
              <Card key={fb.id} className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-900 capitalize">{fb.type.toLowerCase()} Feedback</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${fb.status === 'SUBMITTED' ? 'bg-green-100 text-green-700' : fb.status === 'ACKNOWLEDGED' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{fb.status}</span>
                    </div>
                    {fb.rating != null && (
                      <div className="text-amber-400 mb-1">{'★'.repeat(fb.rating)}{'☆'.repeat(5 - fb.rating)}</div>
                    )}
                    {fb.comment && <p className="text-sm text-gray-600">{fb.comment}</p>}
                    <p className="text-xs text-gray-400 mt-2">{new Date(fb.createdAt).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PortalLayout>
  );
}
